import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import { adminService, type SpeakerWritePayload } from '../../services/adminService'
import type { Speaker } from '../../data/types'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import AssetUploadButton from '../AssetUploadButton'

const COLORS = ['#0075BC', '#1C5580', '#5BC6CC', '#3D6074', '#E47720']

function initialsFrom(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

export default function SpeakersManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Speaker | null>(null)
  const [open, setOpen] = useState(false)

  const load = () =>
    adminService
      .listSpeakers(eventId)
      .then(setSpeakers)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const openNew = () => {
    setEditing(null)
    setOpen(true)
  }
  const openEdit = (s: Speaker) => {
    setEditing(s)
    setOpen(true)
  }

  const remove = async (s: Speaker) => {
    if (!(await confirmDelete(`Delete speaker "${s.name}"?`))) return
    try {
      await adminService.deleteSpeaker(eventId, s.id)
      notify('Speaker deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openNew}>
          Add speaker
        </Button>
      </Stack>

      {speakers.length === 0 ? (
        <EmptyState title="No speakers yet" description="Add the people presenting at this event." />
      ) : (
        <Stack spacing={1.5}>
          {speakers.map((s) => (
            <Card
              key={s.id}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  bgcolor: s.avatarColor || '#0075BC',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  flexShrink: 0,
                  overflow: 'hidden',
                  backgroundImage: s.photoUrl ? `url(${s.photoUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {s.photoUrl ? '' : s.initials || initialsFrom(s.name)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {s.name}
                  {s.featured ? ' ★' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {[s.title, s.company].filter(Boolean).join(', ')}
                </Typography>
              </Box>
              <IconButton onClick={() => openEdit(s)} size="small">
                <EditRoundedIcon />
              </IconButton>
              <IconButton onClick={() => remove(s)} size="small" color="error">
                <DeleteRoundedIcon />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {open && (
        <SpeakerDialog
          eventId={eventId}
          speaker={editing}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

function SpeakerDialog({
  eventId,
  speaker,
  onClose,
  onSaved,
}: {
  eventId: string
  speaker: Speaker | null
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: speaker?.name ?? '',
    title: speaker?.title ?? '',
    company: speaker?.company ?? '',
    bio: speaker?.bio ?? '',
    avatarColor: speaker?.avatarColor ?? COLORS[0],
    photoUrl: speaker?.photoUrl ?? '',
    featured: speaker?.featured ?? false,
  })
  const set = (k: keyof typeof form, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.name) {
      notify('Name is required', 'error')
      return
    }
    const payload: SpeakerWritePayload = {
      name: form.name,
      title: form.title || undefined,
      company: form.company || undefined,
      bio: form.bio || undefined,
      avatarColor: form.avatarColor,
      initials: initialsFrom(form.name),
      photoUrl: form.photoUrl || undefined,
      featured: form.featured,
    }
    setSaving(true)
    try {
      if (speaker) await adminService.updateSpeaker(eventId, speaker.id, payload)
      else await adminService.createSpeaker(eventId, payload)
      notify(speaker ? 'Speaker updated' : 'Speaker added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{speaker ? 'Edit speaker' : 'Add speaker'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <TextField
            label="Title"
            fullWidth
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <TextField
            label="Company"
            fullWidth
            value={form.company}
            onChange={(e) => set('company', e.target.value)}
          />
          <TextField
            label="Bio"
            fullWidth
            multiline
            minRows={3}
            value={form.bio}
            onChange={(e) => set('bio', e.target.value)}
          />
          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Photo URL"
                fullWidth
                value={form.photoUrl}
                onChange={(e) => set('photoUrl', e.target.value)}
                helperText="Optional headshot — paste a URL or upload an image. Leave blank to use the colored initials avatar."
              />
              <AssetUploadButton
                eventId={eventId}
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                label="Upload photo"
                onUploaded={(url) => set('photoUrl', url)}
              />
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Avatar color{form.photoUrl ? ' (used as fallback)' : ''}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {COLORS.map((c) => (
                <Box
                  key={c}
                  onClick={() => set('avatarColor', c)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: c,
                    cursor: 'pointer',
                    border: form.avatarColor === c ? '3px solid' : '3px solid transparent',
                    borderColor: form.avatarColor === c ? 'text.primary' : 'transparent',
                  }}
                />
              ))}
            </Stack>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.featured}
                onChange={(e) => set('featured', e.target.checked)}
              />
            }
            label="Featured speaker"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
